// Auth-сервіс клієнтського порталу.
// Access JWT живе в пам'яті JS (НЕ в localStorage), refresh-токен — у HttpOnly cookie на бекенді.
// При перезавантаженні сторінки bootstrapSession() обмінює cookie на новий access токен.

import { moduleForPermission } from '../modules/registry'

const API_BASE = import.meta.env.VITE_CLIENT_API_BASE || '/client/api'

// localStorage використовується тільки для непрозорого кешу профілю та біллінгу,
// щоб після перезавантаження сторінки одразу показати UI до завершення bootstrap.
const USER_KEY = 'client_portal_user'
const BILLING_STATUS_KEY = 'client_billing_status'

// In-memory access token. Зникає при перезавантаженні — bootstrapSession() відновить.
let accessToken = null
let accessExpiresAt = 0
let bootstrapPromise = null
let refreshInProgress = null

// Поріг автопродовження access JWT — оновлюємо за 60 секунд до експірації
const REFRESH_THRESHOLD_MS = 60 * 1000

// Зберігаємо access токен у пам'яті
export function setAccessToken(token, ttlSeconds) {
  accessToken = token
  accessExpiresAt = Date.now() + (Number(ttlSeconds) || 0) * 1000
}

// Модулі порталу (composables/useModules.js) слухають цю подію і перераховують
// «увімкнено / вимкнено» без перезавантаження, коли профіль оновився
function emitUserChanged() {
  try {
    window.dispatchEvent(new Event('portal:user-changed'))
  } catch {
    // SSR / тести без window
  }
}

// Кладемо спрощеного користувача в localStorage для відображення шапки одразу при старті
export function persistUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user))
  emitUserChanged()
}

// Зберігаємо статус біллінгу клієнта для перевірки доступу
export function persistBillingStatus(billingStatus) {
  if (billingStatus) {
    localStorage.setItem(BILLING_STATUS_KEY, JSON.stringify(billingStatus))
  } else {
    localStorage.removeItem(BILLING_STATUS_KEY)
  }
}

// Локальне очищення стану (БЕЗ виклику /logout на бекенді).
// Використовується при insufficient_role, перед impersonate, при 401 після невдалого refresh.
export function clearAccessToken() {
  accessToken = null
  accessExpiresAt = 0
  localStorage.removeItem(USER_KEY)
  localStorage.removeItem(BILLING_STATUS_KEY)
  emitUserChanged()
}

// Backwards-compat alias — щоб не ламати router/main.js під час міграції
export const clearToken = clearAccessToken

// Returns поточний валідний access токен або null, якщо токен прострочений/відсутній.
// 30-сек запас на жорсткому експірейті, щоб не дотягнути до 401 у мережі.
export function getValidToken() {
  if (accessToken && Date.now() < accessExpiresAt - 30_000) {
    return accessToken
  }
  return null
}

// Есть ли признаки живой сессии: refresh-cookie ещё может обменяться на новый access-токен.
// In-memory токен умирает, когда вкладка провисела в фоне/сне дольше TTL (15 мин) —
// это НЕ разлогин, поэтому падать с token_required в таком случае нельзя.
export function hasRefreshableSession() {
  return localStorage.getItem(USER_KEY) !== null
}

// Гарантированно свежий токен для прямых fetch-запросов (загрузка файлов, экспорт),
// которые идут мимо apiClient и сами обновлять токен не умеют.
// Кидает token_required только если сессию восстановить не удалось.
export async function ensureValidToken() {
  const token = getValidToken()
  if (token) return token
  if (hasRefreshableSession() && (await silentRefresh())) {
    const refreshed = getValidToken()
    if (refreshed) return refreshed
  }
  throw new Error('token_required')
}

// Перевіряє, чи треба фоново оновити токен (залишилось менше 1 хвилини)
export function shouldRefreshToken() {
  if (!accessToken) return false
  return accessExpiresAt - Date.now() < REFRESH_THRESHOLD_MS
}

// Низькорівневий виклик /refresh-token. Cookie auto-надсилається браузером.
// X-Requested-With обходить CSRF guard на бекенді.
async function callRefreshEndpoint() {
  const res = await fetch(`${API_BASE}/refresh-token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
  if (!res.ok) {
    const err = new Error(`refresh_failed_${res.status}`)
    err.status = res.status
    throw err
  }
  return res.json()
}

// Фонове оновлення access токена. Викликається з apiClient при 401 та проактивно перед експірацією.
export async function silentRefresh() {
  if (refreshInProgress) return refreshInProgress
  refreshInProgress = (async () => {
    try {
      const data = await callRefreshEndpoint()
      setAccessToken(data.token, data.accessExpiresIn)
      if (data.user) persistUser(buildUserToSave(data))
      if (data.billingStatus) {
        persistBillingStatus({
          status: data.billingStatus,
          blockReason: data.blockReason || null,
          blockedAt: data.blockedAt || null,
        })
      }
      return true
    } catch {
      return false
    } finally {
      refreshInProgress = null
    }
  })()
  return refreshInProgress
}

// Обмен refresh-токена, который сейчас в полёте (или уже готовый промис, если обмена нет).
// Его ждут перед перезагрузкой страницы: оборванный запрос = сервер токен уже сменил,
// а новая cookie до браузера не дошла, и следующий обмен выглядит как повтор старого токена.
export function whenRefreshSettled() {
  return refreshInProgress || Promise.resolve()
}

// Викликається при старті додатка. Якщо у браузера є валідний refresh cookie —
// відновлює access токен у пам'яті без редиректу на /login.
export async function bootstrapSession() {
  if (bootstrapPromise) return bootstrapPromise
  bootstrapPromise = silentRefresh().finally(() => {
    bootstrapPromise = null
  })
  return bootstrapPromise
}

// Чи є у поточної сторінки якісь ознаки автентифікації (валідний токен у пам'яті
// АБО кеш користувача в localStorage, що означає що bootstrap може вдатися).
// Router використовує цей метод до bootstrap — щоб не редиректити одразу на /login.
export function isAuthenticated() {
  if (getValidToken()) return true
  return localStorage.getItem(USER_KEY) !== null
}

// Запитуємо бекенд клієнта для логіна, зберігаємо access токен (refresh — у cookie з бекенда)
export async function performLogin(username, password, lang, rememberMe = false) {
  // apiRequest імпортуємо тут lazy щоб уникнути циклічного імпорту з apiClient.js
  const { apiRequest } = await import('./apiClient')
  const payload = await apiRequest('/login', {
    method: 'POST',
    body: JSON.stringify({ username, password, lang, rememberMe: !!rememberMe }),
  })

  setAccessToken(payload.token, payload.accessExpiresIn)
  persistUser(buildUserToSave(payload))
  if (payload.billingStatus) {
    persistBillingStatus({
      status: payload.billingStatus,
      blockReason: payload.blockReason || null,
      blockedAt: payload.blockedAt || null,
    })
  }
  return payload
}

// Список активних сесій поточного користувача — кожна сесія = пристрій (family).
// Повертає масив об'єктів з UA-розбором, гео і прапором isCurrent.
export async function fetchActiveSessions() {
  const { apiRequest } = await import('./apiClient')
  const data = await apiRequest('/auth/sessions')
  return data.sessions || []
}

// Відкликає одну конкретну сесію за id. Якщо це поточна сесія — після виклику
// потрібно локально вийти (caller вирішує, бо знає чи це current).
export async function revokeSessionById(sessionId) {
  const { apiRequest } = await import('./apiClient')
  return apiRequest(`/auth/sessions/${sessionId}/revoke`, {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
}

// Список довірених пристроїв (device-trust): мітка client_dt = другий фактор для мережевого
// гейта. Показуються лише «свої» (створені цим користувачем/суб-користувачем).
export async function fetchTrustedDevices() {
  const { apiRequest } = await import('./apiClient')
  const data = await apiRequest('/auth/trusted-devices')
  return data.devices || []
}

// Відкликає довіру до пристрою. Сесію НЕ рве (мітка — не сесійний кредентіал);
// якщо відкликали поточний пристрій, бекенд чистить куку client_dt.
export async function revokeTrustedDevice(deviceId) {
  const { apiRequest } = await import('./apiClient')
  return apiRequest(`/auth/trusted-devices/${deviceId}/revoke`, {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
}

// «Вийти з усіх пристроїв»: відкликає всі активні refresh-токени поточного користувача на бекенді
// (включаючи поточну сесію), потім локально чистить стан. Бекенд знає роль (user/sub-user) з access JWT.
export async function revokeAllSessions() {
  const { apiRequest } = await import('./apiClient')
  try {
    await apiRequest('/auth/revoke-all-sessions', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
  } finally {
    clearAccessToken()
  }
}

// Логічний logout: викликає бекенд для відкликання refresh-токена, потім чистить локальний стан.
// Не кидає помилок навіть якщо мережа недоступна — користувача все одно треба викинути.
export async function performLogout() {
  try {
    await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
  } catch {
    // Тихо ігноруємо: cookie все одно зачистимо локально
  } finally {
    clearAccessToken()
  }
}

// Будує об'єкт користувача для збереження в localStorage з payload бекенда
function buildUserToSave(payload) {
  const userToSave = { ...payload.user }
  if (payload.user?.isSubUser && payload.permissions) {
    userToSave.permissions = payload.permissions
  }
  if (payload.parentDisabledModules) {
    userToSave.parentDisabledModules = payload.parentDisabledModules
  }
  if (payload.impersonated) {
    userToSave.impersonated = true
  }
  return userToSave
}

// Повертає кешованого користувача без повторного запиту
export function getCurrentUser() {
  const raw = localStorage.getItem(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function isDealerUser() {
  const user = getCurrentUser()
  if (user?.isSubUser) {
    return true
  }
  const roles = Array.isArray(user?.roles) ? user.roles : []
  return roles.includes('DEALER_USER')
}

export function isSubUser() {
  const user = getCurrentUser()
  return user?.isSubUser === true
}

// Перевіряє, чи це impersonate-сесія (адмін зайшов як клієнт)
export function isImpersonated() {
  const user = getCurrentUser()
  return user?.impersonated === true
}

// Повертає permissions субкористувача або null для звичайного користувача
export function getSubUserPermissions() {
  const user = getCurrentUser()
  if (!user?.isSubUser) {
    return null
  }
  return user.permissions || {}
}

// Перевіряє чи має користувач конкретний дозвіл.
// Враховує відключені модулі (disabledModules) для основних користувачів
// та parentDisabledModules + персональні дозволи для субкористувачів
export function hasPermission(permissionKey) {
  const user = getCurrentUser()

  // Право належить модулю (modules/registry.js): вимкнений модуль = права немає ні в кого
  const moduleKey = moduleForPermission(permissionKey)
  if (moduleKey) {
    const disabled = user?.disabledModules || []
    if (disabled.includes(moduleKey)) return false
    const parentDisabled = user?.parentDisabledModules || []
    if (parentDisabled.includes(moduleKey)) return false
  }

  if (!user?.isSubUser) {
    return true
  }
  return user.permissions?.[permissionKey] === true
}

// Перевіряє чи увімкнений модуль порталу для поточного клієнта.
// Враховує disabledModules основного користувача та parentDisabledModules субкористувача.
// Ключі модулів — MODULE_KEYS у modules/registry.js (dashboard … personnel, tasks, tacho, dtc).
// У компонентах зручніше реактивний useModules() (composables/useModules.js).
export function isModuleEnabled(moduleKey) {
  const user = getCurrentUser()
  const disabled = user?.disabledModules || []
  if (disabled.includes(moduleKey)) return false
  const parentDisabled = user?.parentDisabledModules || []
  if (parentDisabled.includes(moduleKey)) return false
  return true
}

// Повертає збережений статус біллінгу клієнта
export function getBillingStatus() {
  const raw = localStorage.getItem(BILLING_STATUS_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function isClientBlocked() {
  const billingStatus = getBillingStatus()
  return billingStatus?.status === 'blocked'
}

export function isClientSuspended() {
  const billingStatus = getBillingStatus()
  return billingStatus?.status === 'suspended'
}

export function isClientWarned() {
  const billingStatus = getBillingStatus()
  return billingStatus?.status === 'warned'
}

export function isReadOnlyMode() {
  return isClientSuspended()
}

// Вхід у demo-акаунт без пароля
export async function performDemoLogin(lang) {
  const { apiRequest } = await import('./apiClient')
  const payload = await apiRequest(`/demo-login?lang=${encodeURIComponent(lang || 'uk')}`, {
    method: 'GET',
  })

  setAccessToken(payload.token, payload.accessExpiresIn)
  persistUser(buildUserToSave(payload))
  if (payload.billingStatus) {
    persistBillingStatus({
      status: payload.billingStatus,
      blockReason: payload.blockReason || null,
      blockedAt: payload.blockedAt || null,
    })
  }
  return payload
}

// Обмінює одноразовий impersonate токен на повноцінну сесію
export async function performImpersonateLogin(impersonateToken) {
  const { apiRequest } = await import('./apiClient')
  const payload = await apiRequest('/auth/impersonate', {
    method: 'POST',
    body: JSON.stringify({ token: impersonateToken }),
  })

  setAccessToken(payload.token, payload.accessExpiresIn)
  const userToSave = { ...payload.user, impersonated: true }
  persistUser(userToSave)
  if (payload.billingStatus) {
    persistBillingStatus({
      status: payload.billingStatus,
      blockReason: payload.blockReason || null,
      blockedAt: payload.blockedAt || null,
    })
  }
  return payload
}
